import React from 'react'
import { useTranslation } from '../../utils/localization'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  currentPage = parseInt(currentPage)

  const t = useTranslation()

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return
    onPageChange(page)
  }

  // No need for pagination if there's only one page or no pages
  if (currentPage === 0 || totalPages <= 1) return null

  const generatePageNumbers = () => {
    const pageNumbers = []
    const visiblePages = 2 // Number of visible pages at any time
    let startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2))
    let endPage = Math.min(totalPages, startPage + visiblePages - 1)

    if (endPage - startPage < visiblePages - 1) {
      startPage = Math.max(1, endPage - visiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i)
    }

    return pageNumbers
  }

  return (
    <div className="max-w-[350px] mx-auto flex flex-row gap-2 justify-center items-center space-x-2 mt-4 lg:mt-16 mb-14 md:mb-20 border border-neutral_1 rounded-lg p-2">
      <button
        className="flex items-center space-x-1 text-text"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label={t`Previous Page`}
      >
        <ChevronLeftIcon className="w-5 h-5" />
        <span className="sr-only">{t`Previous`}</span>
      </button>

      {currentPage > 3 && (
        <>
          <button
            className={`px-3 py-1 rounded text-sm text-text ${
              currentPage === 1 ? 'bg-neutral_2 text-white' : 'text-text'
            }`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>
          {currentPage > 4 && <span className="text-text px-3 py-1">...</span>}
        </>
      )}

      {generatePageNumbers().map((page) => (
        <button
          key={page}
          className={`px-3 py-1 rounded text-sm ${
            currentPage === page ? 'bg-neutral_2 text-white' : 'text-text'
          }`}
          onClick={() => handlePageChange(page)}
          aria-current={currentPage === page ? 'page' : undefined}
        >
          {page}
        </button>
      ))}

      {currentPage < totalPages - 2 && (
        <>
          {currentPage < totalPages - 3 && (
            <span className="text-text px-3 py-1">...</span>
          )}
          <button
            className={`px-3 py-1 rounded text-sm text-text ${
              currentPage === totalPages
                ? 'bg-neutral_2 text-white'
                : 'text-text'
            }`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        className="flex items-center space-x-1 text-text"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label={t`Next Page`}
      >
        <span className="sr-only">{t`Next`}</span>
        <ChevronRightIcon className="w-5 h-5" />
      </button>
    </div>
  )
}

export default Pagination
